import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api, type BuildRow, type EhrRow, type SessionRow } from '../lib/api.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { StatCard } from '../components/StatCard.js';
import { PageHeader } from '../components/PageHeader.js';
import { EmptyState } from '../components/EmptyState.js';
import { Badge } from '../components/Badge.js';
import { NewMigrationForm } from '../components/NewMigrationForm.js';

function statusColor(status: string): string {
  switch (status) {
    case 'running': return 'var(--accent-yellow)';
    case 'completed': return 'var(--accent-green)';
    case 'failed': return 'var(--accent-red)';
    default: return 'var(--text-dimmed)';
  }
}

type FilterTab = 'all' | 'running' | 'completed' | 'failed' | 'draft';

export function ImportsPage() {
  const navigate = useNavigate();
  usePageTitle('Imports');
  const qc = useQueryClient();
  const [tab, setTab] = useState<FilterTab>('all');
  const [showNewSession, setShowNewSession] = useState(false);

  const { data: sessionsData } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.listSessions(),
    refetchInterval: 10_000,
  });
  const { data: buildsData } = useQuery({
    queryKey: ['builds'],
    queryFn: () => api.listBuilds(),
  });
  const { data: ehrsData } = useQuery({
    queryKey: ['ehrs'],
    queryFn: () => api.listEhrs(),
  });

  const sessions: SessionRow[] = sessionsData?.sessions ?? [];
  const builds: BuildRow[] = buildsData?.builds ?? [];
  const ehrs: EhrRow[] = ehrsData?.ehrs ?? [];

  const total = sessions.length;
  const running = sessions.filter((s) => s.status === 'running').length;
  const completed = sessions.filter((s) => s.status === 'completed').length;
  const failed = sessions.filter((s) => s.status === 'failed').length;

  const filtered = tab === 'all' ? sessions : sessions.filter((s) => {
    if (tab === 'draft') return s.status === 'draft' || s.status === 'pending';
    return s.status === tab;
  });

  const TABS: { key: FilterTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'running', label: 'Running' },
    { key: 'completed', label: 'Completed' },
    { key: 'failed', label: 'Failed' },
    { key: 'draft', label: 'Draft' },
  ];

  function getEhrName(session: SessionRow): string | null {
    const src = session.sources.find((s) => s.kind === 'ehr');
    if (!src) return null;
    if (src.ehrSlug) {
      const ehr = ehrs.find((e) => e.slug === src.ehrSlug || e.id === src.ehrId);
      return ehr?.displayName ?? src.ehrSlug;
    }
    if (src.ehrId) {
      return ehrs.find((e) => e.id === src.ehrId)?.displayName ?? null;
    }
    if (session.ehrId) {
      return ehrs.find((e) => e.id === session.ehrId)?.displayName ?? null;
    }
    return null;
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Imports"
        title="Sessions"
        actions={
          <>
            <button
              type="button"
              onClick={() => void navigate({ to: '/ehrs' })}
              className="rounded-xl px-4 py-2 text-sm font-semibold transition-opacity duration-150"
              style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(8px)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)' }}
            >
              + Add EHR
            </button>
            <button
              type="button"
              onClick={() => setShowNewSession(true)}
              className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-opacity duration-150"
              style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)', boxShadow: '0 2px 12px rgba(13,207,184,0.3)' }}
            >
              + New Session
            </button>
          </>
        }
      />

      <section className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Stat cards */}
        <div className="grid grid-cols-4 gap-4">
          <StatCard label="Total Sessions" value={String(total)} icon="⧉" colorIndex={0} />
          <StatCard label="Running" value={String(running)} icon="▶" colorIndex={1} />
          <StatCard label="Completed" value={String(completed)} icon="✓" colorIndex={3} />
          <StatCard label="Failed" value={String(failed)} icon="!" colorIndex={2} />
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-1.5">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold capitalize tracking-wide transition-all duration-150"
              style={
                tab === t.key
                  ? { background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)', color: '#fff', boxShadow: '0 2px 8px rgba(13,207,184,0.35)' }
                  : { background: 'var(--glass-bg)', backdropFilter: 'blur(8px)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)' }
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Sessions table */}
        <div
          className="glass rounded-2xl overflow-hidden"
          style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}
        >
          {filtered.length === 0 ? (
            sessions.length === 0 ? (
              <EmptyState
                icon="⧉"
                title="No sessions yet"
                description="Create a new session to begin importing data from an EHR into a Cerbo build."
                action={{ label: '+ New Session', onClick: () => setShowNewSession(true) }}
              />
            ) : (
              <EmptyState
                icon="🔍"
                title={`No ${tab} sessions`}
                description="Try a different filter."
              />
            )
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Build</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Title</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">EHR</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Status</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Started</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const buildName = builds.find((b) => b.id === s.buildId)?.displayName ?? s.buildId ?? '—';
                  const ehrName = getEhrName(s);
                  const ehrId = s.ehrId ?? ehrs.find((e) => e.slug === s.sources.find((src) => src.kind === 'ehr')?.ehrSlug)?.id;
                  return (
                    <tr
                      key={s.id}
                      className="border-b border-border last:border-0 hover:bg-bg-tertiary transition-colors duration-100 cursor-pointer"
                      onClick={() => void navigate({ to: '/sessions/$id', params: { id: s.id } })}
                    >
                      <td className="px-5 py-3 font-mono text-xs text-text-secondary">{buildName}</td>
                      <td className="px-5 py-3">
                        <div className="font-semibold text-text-primary">{s.title ?? s.id.slice(0, 8)}</div>
                        <div className="font-mono text-xs text-text-dimmed">{s.id.slice(0, 8)}</div>
                      </td>
                      <td className="px-5 py-3">
                        {ehrName ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (ehrId) void navigate({ to: '/ehrs/$id', params: { id: ehrId } });
                            }}
                            className="text-xs font-medium hover:underline"
                            style={{ color: 'var(--accent-blue)' }}
                          >
                            {ehrName}
                          </button>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {s.sources.map((src, i) => (
                              <Badge key={i} kind="ehr">{src.kind}</Badge>
                            ))}
                            {s.sources.length > 0 && s.destinations.length > 0 && (
                              <span className="text-text-dimmed text-xs self-center">→</span>
                            )}
                            {s.destinations.map((dst, i) => (
                              <Badge key={i} kind="cerbo">{dst.kind}</Badge>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className="rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase"
                          style={{
                            background: `${statusColor(s.status)}22`,
                            color: statusColor(s.status),
                            border: `1px solid ${statusColor(s.status)}55`,
                          }}
                        >
                          {s.status}
                        </span>
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-text-dimmed">
                        {s.startedAt ? new Date(s.startedAt).toLocaleString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* New Migration modal */}
      {showNewSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div
            className="w-[860px] rounded-2xl p-7"
            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', boxShadow: '0 3px 40px rgba(0,0,0,0.5)' }}
          >
            <div className="flex items-center justify-between mb-5">
              <div className="text-base font-semibold text-text-primary">New Migration</div>
              <button type="button" onClick={() => setShowNewSession(false)} className="text-sm text-text-secondary hover:text-text-primary">✕</button>
            </div>
            <NewMigrationForm
              onCreated={(id) => {
                setShowNewSession(false);
                void qc.invalidateQueries({ queryKey: ['sessions'] });
                void navigate({ to: '/sessions/$id', params: { id } });
              }}
              onCancel={() => setShowNewSession(false)}
            />
          </div>
        </div>
      )}

    </div>
  );
}
